// ═══════════════════════════════════════════════════════════════════════════
// Lógica pura de edición de remisiones (lote 2.5, observación 2)
//
// Funciones sin efectos: se prueban contra las 43 remisiones reales.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convierte una remisión guardada en el estado inicial del editor.
 *
 * `lineItems` replica la forma del editor: TODOS los productos del catálogo,
 * con qty 0 en los que la remisión no trae. Productos de la remisión que ya no
 * existen en el catálogo se reportan en `faltantes` — el editor no puede
 * representarlos y el usuario debe saberlo ANTES de guardar, no descubrirlo
 * comparando PDFs.
 *
 * @param modo 'editar' → conserva el folio (reemplaza al guardar). Cualquier
 *             otro valor deja el folio en null y el editor toma el siguiente.
 *             Se conserva el parámetro porque la precarga sin folio sigue
 *             siendo útil, aunque hoy el único llamador sea Editar: Duplicar
 *             se quitó por confuso (ver dominio/pedido-usual.js).
 */
export function precargaDesdeRemision(inv, products, modo = "editar") {
  const porProducto = new Map((inv.lines || []).map(li => [li.productId, li]));

  // La cortesía viaja con el renglón. Antes solo se cargaba la cantidad, así
  // que editar una remisión con botellas de cortesía las volvía cobrables.
  const lineItems = products.map(p => {
    const li = porProducto.get(p.id);
    return { productId: p.id, qty: li?.qty || 0,
             ...(li?.cortesia ? { cortesia: li.cortesia } : {}) };
  });

  // Los precios CON QUE SE EMITIÓ. Sin ellos el editor volvía a preguntar la
  // lista de precios de hoy, y corregir una cantidad cambiaba el precio de
  // todos los renglones sin que nadie lo notara.
  const precios = {};
  for (const li of inv.lines || []) {
    const pu = Number(li.unitPrice ?? (li.qty ? li.amount / li.qty : NaN));
    if (Number.isFinite(pu)) precios[li.productId] = pu;
  }

  const enCatalogo = new Set(products.map(p => p.id));
  const faltantes = (inv.lines || [])
    .filter(li => !enCatalogo.has(li.productId))
    .map(li => li.productName || li.productId);

  return {
    clientId: inv.client?.id ?? null,
    lineItems,
    faltantes,
    // Se fijan los del DOCUMENTO, no los del cliente actual: son parte de lo
    // que se está editando. El usuario puede quitarlos en el editor.
    discount: inv.discount ?? null,
    ivaRate: inv.ivaRate ?? null,
    notes: inv.notes || "",
    folio: modo === "editar" ? inv.folio : null,
    // Lo fiscal y la bodega, también del documento.
    formaPago: inv.formaPago || null,
    metodoPago: inv.metodoPago || null,
    usoCfdi: inv.usoCfdi || null,
    regimenFiscal: inv.regimenFiscal || null,
    warehouse: inv.warehouse || null,
    // La identidad de lo que se edita: el mismo documento, con su folio y su
    // fecha de emisión. Solo al editar.
    edicion: modo === "editar"
      ? { id: inv.id, folio: inv.folio, date: inv.date,
          clientId: inv.client?.id ?? null, precios }
      : null,
  };
}

/**
 * Los renglones cuyo precio de hoy ya no es el de la emisión. Al abrir una
 * remisión para corregirla se le pregunta al operador si la actualiza: cambiar
 * la lista de precios y luego corregir la remisión es justo para eso, y antes
 * se conservaba el precio viejo sin decir nada.
 *
 * `precioDeHoy(productId)` es el precio que tendría hoy para su cliente. Los
 * productos que ya no están en el catálogo no se cargan, así que no cuentan.
 */
export function preciosQueCambiaron(inv, precios, products, precioDeHoy) {
  const enCatalogo = new Set(products.map(p => p.id));
  return (inv.lines || [])
    .filter(li => enCatalogo.has(li.productId) && precios?.[li.productId] !== undefined)
    .map(li => ({ productId: li.productId, nombre: li.productName || li.productId,
                  antes: Number(precios[li.productId]), hoy: Number(precioDeHoy(li.productId)) }))
    .filter(c => Number.isFinite(c.hoy) && Math.abs(c.antes - c.hoy) >= 0.005);
}

/**
 * Qué cambia entre la remisión guardada y la que se va a guardar, en frases
 * para el operador. Es lo que se le enseña ANTES de confirmar: lo que el
 * mensaje genérico de antes no decía, y por eso se aceptaba sin leer.
 */
export function describirCambios(antes, despues) {
  const cambios = [];
  const nombre = (li) => li.productName || li.productId;
  const fmtN = (n) => Number(n || 0).toLocaleString("es-MX",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if ((antes.client?.id ?? null) !== (despues.client?.id ?? null))
    cambios.push(`Cliente: ${antes.client?.name || "—"} → ${despues.client?.name || "—"}`);

  const a = new Map((antes.lines || []).filter(l => l.qty > 0).map(l => [l.productId, l]));
  const d = new Map((despues.lines || []).filter(l => l.qty > 0).map(l => [l.productId, l]));
  for (const [id, l] of d) {
    const v = a.get(id);
    if (!v) { cambios.push(`+ ${nombre(l)}: ${l.qty}`); continue; }
    if (v.qty !== l.qty) cambios.push(`${nombre(l)}: ${v.qty} → ${l.qty}`);
    if ((v.cortesia || 0) !== (l.cortesia || 0))
      cambios.push(`${nombre(l)}, cortesía: ${v.cortesia || 0} → ${l.cortesia || 0}`);
    if (Math.abs(Number(v.unitPrice) - Number(l.unitPrice)) > 0.004)
      cambios.push(`${nombre(l)}, precio: $${fmtN(v.unitPrice)} → $${fmtN(l.unitPrice)}`);
  }
  for (const [id, v] of a) if (!d.has(id)) cambios.push(`− ${nombre(v)}: ${v.qty} (se quita)`);

  if (Number(antes.discount || 0) !== Number(despues.discount || 0))
    cambios.push(`Descuento: ${antes.discount || 0}% → ${despues.discount || 0}%`);
  if (Number(antes.ivaRate ?? 16) !== Number(despues.ivaRate ?? 16))
    cambios.push(`IVA: ${antes.ivaRate ?? 16}% → ${despues.ivaRate ?? 16}%`);
  if ((antes.notes || "") !== (despues.notes || "")) cambios.push("Notas");
  if (Math.abs(Number(antes.total || 0) - Number(despues.total || 0)) > 0.004)
    cambios.push(`Total: $${fmtN(antes.total)} → $${fmtN(despues.total)}`);
  return cambios;
}

/**
 * Cuánto stock descontar al guardar, considerando si REEMPLAZA una remisión.
 *
 * Corrige un defecto del sistema actual: al reemplazar por folio, el stock se
 * descontaba COMPLETO otra vez (guardar dos veces la misma remisión de 12
 * botellas descontaba 24). Con delta, editar qty 12→15 desconta 3; 12→10
 * devuelve 2.
 *
 * @returns Map productId → cantidad a descontar (negativa = devolver)
 */
export function ajusteDeStock(remisionAnterior, remisionNueva) {
  const delta = new Map();
  for (const li of remisionNueva.lines || []) {
    if (li.qty > 0) delta.set(li.productId, (delta.get(li.productId) || 0) + li.qty);
  }
  for (const li of remisionAnterior?.lines || []) {
    if (li.qty > 0) delta.set(li.productId, (delta.get(li.productId) || 0) - li.qty);
  }
  for (const [k, v] of delta) if (v === 0) delta.delete(k);
  return delta;
}

/**
 * ¿Se puede editar esta remisión? Regla acordada: con CFDI vigente no hay
 * edición silenciosa — la corrección real es cancelar (motivo 01) y timbrar
 * el sustituto. Eso es Etapa 5; mientras, se explica en vez de permitir.
 */
export function puedeEditarse(inv) {
  if (inv.cfdiId && inv.cfdiStatus !== "canceled") {
    return {
      ok: false,
      motivo:
        `La remisión #${inv.folio} tiene un CFDI TIMBRADO ante el SAT.\n\n` +
        `Editarla aquí no cambiaría el comprobante fiscal: seguiría existiendo ` +
        `con los datos originales.\n\n` +
        `La corrección correcta es cancelar el CFDI (motivo 01) y timbrar uno ` +
        `sustituto. Mientras esa función llega, puedes:\n` +
        `  • Cancelar el CFDI desde aquí y corregir esta misma remisión\n` +
        `  • Capturar una remisión nueva con folio nuevo — al elegir el ` +
        `cliente se ofrece su pedido usual`,
    };
  }
  return { ok: true };
}

/**
 * ¿Se puede SOBRESCRIBIR la remisión que ya ocupa este folio?
 *
 * Es la misma pregunta que `puedeEditarse`, pero desde el otro lado: el editor
 * permite teclear un folio a mano, así que se puede llegar a una remisión sin
 * haber pasado por el botón Editar. Este es el control que de verdad protege
 * el documento; el botón solo es la puerta de enfrente.
 *
 * Antes cada botón de guardar traía su propia comprobación —`existing.cfdiId`
 * y nada más— y eso dejaba dos huecos:
 *
 *   · una remisión CANCELADA se podía sobrescribir, porque no tenía cfdiId.
 *     Al reemplazarla revivía: desaparecían el motivo, la fecha y quién la
 *     canceló. El folio quemado dejaba de estar quemado. Es justo el rastro
 *     que la cancelación existe para conservar.
 *
 *   · una remisión con el CFDI ya CANCELADO se bloqueaba, aunque
 *     `puedeEditarse` la considera editable otra vez. El botón Editar la
 *     abría y el guardado la rechazaba: un callejón sin salida.
 */
export function puedeReemplazarse(existente) {
  if (!existente) return { ok: true };

  if (existente.canceledAt) {
    return {
      ok: false,
      motivo:
        `La remisión #${existente.folio} está CANCELADA.\n\n` +
        `Sobrescribirla borraría el motivo, la fecha y quién la canceló, y el ` +
        `folio dejaría de estar consumido. Ese rastro es justamente para lo ` +
        `que sirve cancelar en vez de borrar.\n\n` +
        `Usa un folio nuevo.`,
    };
  }

  // Con CFDI vigente no se toca. Con el CFDI ya cancelado sí: el comprobante
  // dejó de existir ante el SAT y el documento vuelve a ser corregible.
  return puedeEditarse(existente);
}

// ── El catálogo en orden alfabético ────────────────────────────────────────
//
// El operador: "El inventario se despliega de manera aleatoria cuando se va a
// hacer o a editar un pedido, una remisión. Debería aparecer siempre en orden
// alfabético."
//
// No era aleatorio, pero lo parecía: era el orden en que Postgres devolvía las
// filas de `product`, que sin `order by` no promete ninguno y cambia cuando
// una fila se actualiza. Editar el precio de un vino lo mandaba al final.
//
// Se ordena aquí y no en la lectura del catálogo: la lista de precios y el
// cuadernillo tienen su propio orden, y no se les cambia sin pedirlo.
//
// Orden del español: sin distinguir mayúsculas ni acentos, con la ñ después de
// la n. Los empates se resuelven por id para que el orden no dependa de cómo
// llegaron las filas.
const COTEJO = new Intl.Collator("es", { sensitivity: "base" });

export function ordenarCatalogo(products = []) {
  return [...products].sort((a, b) =>
    COTEJO.compare(String(a?.name ?? "").trim(), String(b?.name ?? "").trim()) ||
    String(a?.id ?? "").localeCompare(String(b?.id ?? "")));
}
